import { useEffect, useRef, useCallback, memo } from 'react';
import { useExperienceMode } from '@/contexts/ExperienceModeContext';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/i18n/LanguageContext';

/**
 * Hidden discovery triggers for Alternate Mode - Optimized version
 * - Triple tap on empty space
 * - Long press (3s) on title
 * - Specific scroll pattern (down, up, down quickly)
 * - Being idle for 3 minutes then moving suddenly
 */
const AlternateModeDiscovery = memo(() => {
  const { isDiscovered, discoverAlternateMode } = useExperienceMode();
  const { t } = useLanguage();
  
  const tapCountRef = useRef(0);
  const lastTapTimeRef = useRef(0);
  const scrollPatternRef = useRef<('up' | 'down')[]>([]);
  const lastScrollYRef = useRef(0);
  const idleStartRef = useRef(Date.now());
  const isIdleRef = useRef(false);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const idleIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const showDiscoveryToast = useCallback((source: string) => {
    toast({
      title: t('something_shifted'),
      description: t('reality_feels_different'),
      duration: 4000,
    });
    discoverAlternateMode(source);
  }, [discoverAlternateMode, t]);

  // Triple tap detection - passive listeners
  useEffect(() => {
    if (isDiscovered) return;

    const handleTap = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, a, input, textarea, [role="button"]')) return;

      const now = Date.now();
      if (now - lastTapTimeRef.current < 400) {
        tapCountRef.current++;
        if (tapCountRef.current >= 3) {
          showDiscoveryToast('triple-tap');
          tapCountRef.current = 0;
        }
      } else {
        tapCountRef.current = 1;
      }
      lastTapTimeRef.current = now;
    };

    window.addEventListener('click', handleTap, { passive: true });
    window.addEventListener('touchend', handleTap, { passive: true });

    return () => {
      window.removeEventListener('click', handleTap);
      window.removeEventListener('touchend', handleTap);
    };
  }, [isDiscovered, showDiscoveryToast]);

  // Scroll pattern detection - throttled with passive listener
  useEffect(() => {
    if (isDiscovered) return;

    let ticking = false;
    
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      
      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const direction = currentY > lastScrollYRef.current ? 'down' : 'up';
        
        if (scrollPatternRef.current[scrollPatternRef.current.length - 1] !== direction) {
          scrollPatternRef.current.push(direction);
          
          const pattern = scrollPatternRef.current.slice(-3);
          if (pattern.length === 3 && 
              pattern[0] === 'down' && 
              pattern[1] === 'up' && 
              pattern[2] === 'down') {
            showDiscoveryToast('scroll-pattern');
            scrollPatternRef.current = [];
          }
          
          if (scrollPatternRef.current.length > 5) {
            scrollPatternRef.current = scrollPatternRef.current.slice(-5);
          }
        }
        
        lastScrollYRef.current = currentY;
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDiscovered, showDiscoveryToast]);

  // Idle detection - reduced interval frequency
  useEffect(() => {
    if (isDiscovered) return;

    const IDLE_THRESHOLD = 180000; // 3 minutes

    const handleActivity = () => {
      if (isIdleRef.current) {
        showDiscoveryToast('idle-awakening');
      }
      idleStartRef.current = Date.now();
      isIdleRef.current = false;
    };

    // Check less frequently (every 30 seconds instead of 10)
    idleIntervalRef.current = setInterval(() => {
      if (Date.now() - idleStartRef.current > IDLE_THRESHOLD) {
        isIdleRef.current = true;
      }
    }, 30000);

    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('touchstart', handleActivity, { passive: true });

    return () => {
      if (idleIntervalRef.current) clearInterval(idleIntervalRef.current);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [isDiscovered, showDiscoveryToast]);

  // Long press detection
  useEffect(() => {
    if (isDiscovered) return;

    const handleLongPressStart = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-alternate-trigger]')) return;

      longPressTimerRef.current = setTimeout(() => {
        showDiscoveryToast('long-press');
      }, 3000);
    };

    const handleLongPressEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    window.addEventListener('mousedown', handleLongPressStart, { passive: true });
    window.addEventListener('touchstart', handleLongPressStart, { passive: true });
    window.addEventListener('mouseup', handleLongPressEnd, { passive: true });
    window.addEventListener('touchend', handleLongPressEnd, { passive: true });

    return () => {
      window.removeEventListener('mousedown', handleLongPressStart);
      window.removeEventListener('touchstart', handleLongPressStart);
      window.removeEventListener('mouseup', handleLongPressEnd);
      window.removeEventListener('touchend', handleLongPressEnd);
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, [isDiscovered, showDiscoveryToast]);

  return null;
});

AlternateModeDiscovery.displayName = 'AlternateModeDiscovery';

export default AlternateModeDiscovery;